import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { forkJoin, Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { Table } from 'primeng/table'
import { AccountSubjectGroupDto, IdentityProviderDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { UserManagementItem } from '../user-management/user-management.component'
import { ApiService } from 'src/app/services/api.service'
import { ModalController, ModalService } from '../../services/modal.service'
import { BaseUserModal, GroupItem, RoleItem } from '../shared/base-user-modal'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService } from 'src/app/services/role-loading.service'

@Component({
  selector: 'app-edit-user-modal',
  templateUrl: './edit-user-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditUserModalComponent extends BaseUserModal implements OnDestroy, ModalController {
  @ViewChild('editUserModal') modal: ModalDialogComponent
  @ViewChild('rolesTable') rolesTable: Table
  @ViewChild('groupsTable') groupsTable: Table
  @Input() user: UserManagementItem | null = null
  @Output() userUpdated = new EventEmitter<UserManagementItem>()

  // True when the row being edited is the logged-in user. Name editing is only offered for your own
  // (native) account, since the account-subjects endpoint can't persist another user's name.
  isSelf = false

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    protected uiService: UiService,
    protected apiService: ApiService,
    private modalService: ModalService,
    public accessLevelService: AccessLevelService,
    protected roleLoadingService: RoleLoadingService
  ) {
    super(formBuilder, accountService, apiService, uiService, accessLevelService, roleLoadingService)
    // Register with modal service
    this.modalService.register(this, this)
  }

  /**
   * Returns the list of identity providers available for SSO user management.
   * Filters out MS Entra IdPs that are not configured to be used for authentication.
   */
  get ssoIdentityProviders(): IdentityProviderDto[] {
    return this.accountService.listSsoIdentityProviders()
  }

  ngOnDestroy() {
    this.cleanup()
    // Unregister from modal service
    this.modalService.unregister(this)
  }

  // Override form initialization to include additional fields
  protected initializeForm() {
    this.userForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      identityProvider: [''],
    })
  }

  // Abstract method implementations
  readonly dialogId = 'edit-user-modal'

  getSaveButtonText(): string {
    return this.saving ? 'Saving...' : 'Save Changes'
  }

  performSave(userData: any): Observable<any> {
    // First update the user's basic info and direct role assignments
    const userUpdate$ = this.apiService.updateAccountSubject(
      this.accountService.accountId,
      this.user!.accountSubjectId,
      userData
    )

    // Then handle group membership changes
    return userUpdate$.pipe(
      switchMap((updatedUser) => {
        return this.updateGroupMemberships().pipe(
          map(() => updatedUser) // Return the updated user
        )
      }),
      // Name lives on the native user, not the account subject, so it must go through the users endpoint
      // and only when you're editing your own account.
      switchMap((updatedUser) => {
        const subjectId = this.user?.subject?.subjectId
        if (this.canEditName && this.nameChanged && subjectId) {
          return this.apiService.updateUser(subjectId, { name: userData.name }).pipe(map(() => updatedUser))
        }
        return of(updatedUser)
      })
    )
  }

  private updateGroupMemberships(): Observable<any> {
    if (!this.user) return of(null)

    // Get current groups that contain this user
    const currentGroupIds = this.availableGroups
      .filter((group) => group.accountSubjects?.includes(this.user!.accountSubjectId))
      .map((group) => group.id)

    // Get selected group IDs
    const selectedGroupIds = this.selectedGroups.map((group) => group.id)

    // Find groups to add user to (selected but not currently in)
    const groupsToJoin = selectedGroupIds.filter((id) => !currentGroupIds.includes(id))

    // Find groups to remove user from (currently in but not selected)
    const groupsToLeave = currentGroupIds.filter((id) => !selectedGroupIds.includes(id))

    const updates: Observable<any>[] = []

    // Add user to new groups - need to fetch full group data first
    groupsToJoin.forEach((groupId) => {
      const getAndUpdateGroup$ = this.apiService.getAccountSubjectGroup(this.accountService.accountId, groupId).pipe(
        switchMap((fullGroup: AccountSubjectGroupDto) => {
          // Only send fields that can be updated - exclude server-managed timestamps
          // roles is already a string[] according to AccountSubjectGroupDto interface
          const updatedGroup: any = {
            name: fullGroup.name,
            externalId: fullGroup.externalId,
            accountSubjects: [...(fullGroup.accountSubjects || []), this.user!.accountSubjectId],
            // Do NOT send: accountSubjectGroupId, accountId, createdAt, lastUpdatedAt
          }

          // Only include roles if they exist and are not empty
          if (fullGroup.roles && fullGroup.roles.length > 0) {
            updatedGroup.roles = fullGroup.roles
          }

          return this.apiService.updateAccountSubjectGroup(this.accountService.accountId, groupId, updatedGroup)
        })
      )
      updates.push(getAndUpdateGroup$)
    })

    // Remove user from old groups - need to fetch full group data first
    groupsToLeave.forEach((groupId) => {
      const getAndUpdateGroup$ = this.apiService.getAccountSubjectGroup(this.accountService.accountId, groupId).pipe(
        switchMap((fullGroup: AccountSubjectGroupDto) => {
          // Only send fields that can be updated - exclude server-managed timestamps
          // roles is already a string[] according to AccountSubjectGroupDto interface
          const updatedGroup: any = {
            name: fullGroup.name,
            externalId: fullGroup.externalId,
            accountSubjects: (fullGroup.accountSubjects || []).filter((id) => id !== this.user!.accountSubjectId),
            // Do NOT send: accountSubjectGroupId, accountId, createdAt, lastUpdatedAt
          }

          // Only include roles if they exist and are not empty
          if (fullGroup.roles && fullGroup.roles.length > 0) {
            updatedGroup.roles = fullGroup.roles
          }
          return this.apiService.updateAccountSubjectGroup(this.accountService.accountId, groupId, updatedGroup)
        })
      )
      updates.push(getAndUpdateGroup$)
    })

    // Execute all group updates in parallel
    return updates.length > 0 ? forkJoin(updates) : of(null)
  }

  onSaveSuccess(): void {
    this.close()
    this.userUpdated.emit(this.user!) // Notify parent component to refresh user data
  }

  getFormData(): any {
    const formData = this.userForm.value
    return {
      name: formData.name,
      email: formData.email,
      identityProviderId: formData.identityProvider,
      roles: this.selectedRoles.map((role) => role.id), // Send role IDs array
      // Note: Groups are managed separately - user membership is stored on the group, not the user
    }
  }

  open(user: UserManagementItem, isSelf = false) {
    this.user = user
    this.isSelf = isSelf
    this.populateForm()
    this.loadRolesAndGroups().then(() => {
      this.loadUserRolesAndGroups()
      this.captureInitialState()
      this.modal.open()
    })
  }

  /** Name is only editable for your own native account; otherwise the control is disabled. */
  get canEditName(): boolean {
    return this.isSelf && !!this.user && this.isNativeArpioUser(this.user)
  }

  get nameChanged(): boolean {
    return this.canEditName && this.userForm.get('name')?.value !== this.user?.name
  }

  override get canSave(): boolean {
    return this.isFormValid && (this.hasChanges || this.nameChanged)
  }

  close() {
    this.modal.close()
    this.resetForm()
  }

  isModalOpen(): boolean {
    return this.modal && this.modal.isOpen
  }

  private populateForm() {
    if (this.user) {
      // Default to Arpio if user has no identity provider and it's the only option
      let defaultIdp = this.user.subject?.identityProviderId || ''
      if (!defaultIdp) {
        const idps = this.ssoIdentityProviders
        if (idps.length === 1) {
          defaultIdp = idps[0].identityProviderId
        }
      }

      this.userForm.patchValue({
        name: this.user.name,
        email: this.user.email,
        identityProvider: defaultIdp,
      })
    }
  }

  private loadUserRolesAndGroups() {
    if (!this.user) return

    // Load user's directly assigned roles
    const directRoleIds = this.user.subject.roles || []

    // Load user's SSO roles (read-only)
    const ssoRoleIds = this.user.ssoRoles?.map((role) => role.id) || []

    // Load user's groups - find groups that contain this user
    this.selectedGroups = this.availableGroups.filter(
      (group) => group.accountSubjects && group.accountSubjects.includes(this.user!.accountSubjectId)
    )

    // Get SSO group IDs (read-only)
    const ssoGroupIds = this.user.ssoGroups?.map((group) => group.id) || []

    // Collect all role IDs from groups and track which groups provide each role
    const groupRoleIds = new Set<string>()
    const roleToGroupsMap = new Map<string, string[]>()

    this.selectedGroups.forEach((group) => {
      if (group.roles) {
        group.roles.forEach((roleId) => {
          groupRoleIds.add(roleId)
          if (!roleToGroupsMap.has(roleId)) {
            roleToGroupsMap.set(roleId, [])
          }
          roleToGroupsMap.get(roleId)!.push(group.name)
        })
      }
    })

    // Reset all roles source information
    this.availableRoles.forEach((role) => {
      role.source = undefined
      role.groupNames = undefined
      role.selected = false
    })

    // Update roles with source information
    this.availableRoles.forEach((role) => {
      const isDirectRole = directRoleIds.includes(role.id)
      const isGroupRole = groupRoleIds.has(role.id)
      const isSsoRole = ssoRoleIds.includes(role.id)

      if (isDirectRole && isGroupRole) {
        // Role is both directly assigned and inherited from group
        role.source = 'direct'
        role.groupNames = roleToGroupsMap.get(role.id) || []
        role.selected = true
      } else if (isDirectRole) {
        // Role is only directly assigned
        role.source = 'direct'
        role.selected = true
      } else if (isGroupRole) {
        // Role is only inherited from group
        role.source = 'group'
        role.groupNames = roleToGroupsMap.get(role.id) || []
        role.selected = false // Group roles are not directly editable
      } else if (isSsoRole) {
        // Role is from SSO provider (read-only)
        role.source = 'sso'
        role.selected = false // SSO roles are not directly editable
      }
    })

    // Only directly assigned roles should be in selectedRoles for editing
    this.selectedRoles = this.availableRoles.filter((role) => directRoleIds.includes(role.id))

    // Update the selected property for PrimeNG table display for groups
    this.availableGroups.forEach((group) => {
      const isManualGroup = group.accountSubjects && group.accountSubjects.includes(this.user!.accountSubjectId)
      const isSsoGroup = ssoGroupIds.includes(group.id)

      group.selected = isManualGroup

      // Mark SSO groups specially if needed
      if (isSsoGroup && !isManualGroup) {
        group.source = 'sso'
      }
    })
  }

  private resetForm() {
    this.userForm.reset()
    this.resetSelections()
    this.saving = false
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe()
      this.saveSubscription = null
    }
  }

  roleTrackBy(_index: number, role: RoleItem) {
    return role.id
  }

  groupTrackBy(_index: number, group: GroupItem) {
    return group.id
  }

  isNativeArpioUser(user: UserManagementItem): boolean {
    if (!user?.subject?.identityProviderId) {
      return false
    }
    const idp = this.accountService.getIdentityProvider(user.subject.identityProviderId)
    return idp?.type?.toLowerCase() === 'native'
  }

  isSamlUser(): boolean {
    return this.user ? !this.isNativeArpioUser(this.user) : false
  }

  customSort = this.accessLevelService.createAccessLevelSortFunction()

  filterRoles(event: any) {
    if (this.rolesTable) {
      this.rolesTable.filterGlobal(event.target.value, 'contains')
    }
  }

  filterGroups(event: any) {
    if (this.groupsTable) {
      this.groupsTable.filterGlobal(event.target.value, 'contains')
    }
  }

  // Generate tooltip content for group inheritance with badge styling
  getGroupInheritanceTooltip(groupNames: string[], prefix: string = 'Inherited from group(s):'): string {
    if (!groupNames || groupNames.length === 0) return ''

    const badgeHtml = groupNames.map((name) => `<span class="tooltip-badge">${name}</span>`).join('')
    return `${prefix} ${badgeHtml}`
  }
}
